'use client';

import { useEffect, useRef, useState } from 'react';

import type { CourseCardCartItem, CourseCardCartState, CourseCardItem } from '../types/course-card';
import { mapCourseCardToCartItem } from '../lib/course-card-stage-pricing-utils';

const FLASH_ADDED_TIMEOUT_MS = 2000;

interface UseCourseCardInteractionParams {
  course: CourseCardItem;
  isInCart: boolean;
  onAddToCart?: (course: CourseCardItem, cartItem: CourseCardCartItem) => void;
  onCheckout?: (course: CourseCardItem, cartItem: CourseCardCartItem) => void;
}

interface CourseCardInteraction {
  cartItem: CourseCardCartItem;
  cartState: CourseCardCartState;
  handleCartAction: () => void;
}

export function useCourseCardInteraction({
  course,
  isInCart,
  onAddToCart,
  onCheckout,
}: UseCourseCardInteractionParams): CourseCardInteraction {
  const [flashAdded, setFlashAdded] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const cartItem = mapCourseCardToCartItem(course);

  useEffect(
    () => () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    },
    [],
  );

  const handleCartAction = () => {
    if (isInCart) {
      onCheckout?.(course, cartItem);
      return;
    }

    onAddToCart?.(course, cartItem);
    setFlashAdded(true);

    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => {
      setFlashAdded(false);
      flashTimer.current = null;
    }, FLASH_ADDED_TIMEOUT_MS);
  };

  let cartState: CourseCardCartState = 'idle';
  if (isInCart) {
    cartState = flashAdded ? 'flash' : 'pay';
  } else if (flashAdded) {
    cartState = 'flash';
  }

  return { cartItem, cartState, handleCartAction };
}
